import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../../hooks/GlobalProvider";
import { FaCheckCircle, FaTimesCircle, FaTruckLoading, FaEye, FaEdit, FaTrash, FaImage, FaBox, FaPlus, FaUpload } from "react-icons/fa";
import { Link } from "react-router-dom";
import { MdPendingActions } from "react-icons/md";
import { PRODUCT_LISTING } from "../../../hooks/APIHooks";

const ProductList = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedApproval, setSelectedApproval] = useState("all");
  const [expandedProduct, setExpandedProduct] = useState(null);

  useEffect(() => {
    if (user) {
      fetchProducts();
    }
  }, [user]);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(PRODUCT_LISTING);
      const productsData = response.data.data || response.data || [];
      setProducts(Array.isArray(productsData) ? productsData : []);
    } catch (error) {
      console.error("Error fetching products:", error);
      setError("Error fetching products.");
    } finally {
      setLoading(false);
    }
  };

  const handleNewArrivalChange = async (productId, isChecked) => {
    try {
      const response = await axios.put(PRODUCT_LISTING, {
        product_id: productId,
        new_arrival: isChecked ? 1 : 0,
      });

      if (response.data.success) {
        setProducts((prevProducts) =>
          prevProducts.map((product) =>
            product.product_id === productId
              ? { ...product, new_arrival: isChecked ? 1 : 0 }
              : product
          )
        );
      } else {
        setError(response.data.error || "Failed to update new arrival status");
      }
    } catch (error) {
      console.error("Error updating new arrival status:", error);
      setError("Error updating new arrival status.");
    }
  };

  const handleApprovalChange = async (productId, newApproval) => {
    try {
      const response = await axios.put(PRODUCT_LISTING, {
        product_id: productId,
        approval: newApproval,
      });
      
      if (response.data.success) {
        setProducts((prevProducts) =>
          prevProducts.map((product) =>
            product.product_id === productId
              ? { ...product, approval: newApproval }
              : product
          )
        );
      } else {
        setError(response.data.error || "Failed to update approval status");
      }
    } catch (error) {
      console.error("Error updating approval status:", error);
      setError("Error updating approval status.");
    }
  };

  const handleDeleteProduct = async (product) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this product?"
    );
    if (!confirmDelete) return;

    try {
      const response = await axios.delete(PRODUCT_LISTING, {
        data: {
          product_id: product.product_id,
          product_type: product.product_type || 'single',
          sku: product.sku,
        },
      });

      if (response.data.success) {
        setProducts((prevProducts) =>
          prevProducts.filter((p) => p.product_id !== product.product_id)
        );
      } else {
        setError(response.data.error || "Failed to delete product");
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      setError("Error deleting product.");
    }
  };

  // Function to get proper image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    
    const cleanPath = imagePath.replace(/^(\.?\/?)?/, '');
    let baseUrl;
    const { hostname, port, protocol } = window.location;
    
    if (hostname === 'localhost' && port === '3000') {
      baseUrl = `${protocol}//${hostname}`;
    } else {
      baseUrl = window.location.origin;
    }
    
    return `${baseUrl}/apis/${cleanPath}`;
  };

  // Filter products based on selected approval status
  const filteredProducts = selectedApproval === "all" 
    ? products 
    : products.filter((product) => product.approval === selectedApproval);

  // Get approval badge component
  const getApprovalBadge = (approval) => {
    switch (approval) {
      case "approved":
        return (
          <span className="flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1.5 rounded-full text-xs font-semibold border border-green-200">
            <FaCheckCircle className="text-green-500 text-xs" />
            <span>Approved</span>
          </span>
        );
      case "disapproved":
        return (
          <span className="flex items-center gap-1.5 bg-red-50 text-red-700 px-3 py-1.5 rounded-full text-xs font-semibold border border-red-200">
            <FaTimesCircle className="text-red-500 text-xs" />
            <span>Disapproved</span>
          </span>
        );
      case "pending":
        return (
          <span className="flex items-center gap-1.5 bg-yellow-50 text-yellow-700 px-3 py-1.5 rounded-full text-xs font-semibold border border-yellow-200">
            <MdPendingActions className="text-yellow-500 text-xs" />
            <span>Pending</span>
          </span>
        );
      case "processing":
        return (
          <span className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-xs font-semibold border border-blue-200">
            <FaTruckLoading className="text-blue-500 text-xs" />
            <span>Processing</span>
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1.5 bg-gray-50 text-gray-700 px-3 py-1.5 rounded-full text-xs font-semibold border border-gray-200">
            <span>{approval}</span>
          </span>
        );
    }
  };

  // Toggle product details expansion
  const toggleProductExpansion = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  // Get stock status badge
  const getStockBadge = (quantity) => {
    if (quantity > 20) {
      return (
        <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-2.5 py-1 rounded-full text-xs font-medium border border-green-200">
          <FaBox className="text-green-500 text-xs" />
          {quantity} in stock
        </span>
      );
    } else if (quantity > 0) {
      return (
        <span className="inline-flex items-center gap-1 bg-yellow-50 text-yellow-700 px-2.5 py-1 rounded-full text-xs font-medium border border-yellow-200">
          <FaBox className="text-yellow-500 text-xs" />
          {quantity} left
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 px-2.5 py-1 rounded-full text-xs font-medium border border-red-200">
          <FaBox className="text-red-500 text-xs" />
          Out of stock
        </span>
      );
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Product Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            Welcome, {user ? user.name : "User"}! Manage your products efficiently.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-2">
          <Link
            to="/auth/bulk-upload"
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-2"
          >
            <FaUpload className="text-sm" />
            Bulk Upload
          </Link>
          <Link
            to="/auth/add-product"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-2"
          >
            <FaPlus className="text-sm" />
            Add Product
          </Link>
        </div>
      </div>

      {/* Filter Buttons - Horizontal Tabs */}
      <div className="mb-6 bg-white rounded-xl border border-gray-200 p-2 shadow-sm">
        <div className="flex flex-wrap gap-1">
          {[
            { key: "all", label: "All Products", count: products.length },
            { key: "pending", label: "Pending", count: products.filter(p => p.approval === "pending").length },
            { key: "processing", label: "Processing", count: products.filter(p => p.approval === "processing").length },
            { key: "approved", label: "Approved", count: products.filter(p => p.approval === "approved").length },
            { key: "disapproved", label: "Disapproved", count: products.filter(p => p.approval === "disapproved").length }
          ].map((status) => (
            <button
              key={status.key}
              onClick={() => setSelectedApproval(status.key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                selectedApproval === status.key
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              <span>{status.label}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                selectedApproval === status.key
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-600"
              }`}>
                {status.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-700">
              <FaTimesCircle className="text-red-500" />
              <span className="text-sm font-medium">{error}</span>
            </div>
            <button 
              onClick={() => setError(null)} 
              className="text-red-500 hover:text-red-700 text-lg font-bold"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600 text-lg">Loading products...</span>
        </div>
      ) : filteredProducts.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wider">
            <div className="col-span-3 flex items-center gap-2">
              <FaImage className="text-gray-400 text-xs" />
              Product
            </div>
            <div className="col-span-2 text-center">Status</div>
            <div className="col-span-2 text-center">Inventory</div>
            <div className="col-span-2 text-center">Category</div>
            <div className="col-span-2 text-center">Price</div>
            <div className="col-span-1 text-center">Actions</div>
          </div>

          {/* Product List */}
          <div className="divide-y divide-gray-100">
            {filteredProducts.map((product) => {
              const isExpanded = expandedProduct === product.product_id;
              
              return (
                <div key={product.product_id} className="transition-colors hover:bg-gray-50/50">
                  {/* Main Product Row */}
                  <div className="grid grid-cols-12 gap-4 px-6 py-4 items-center">
                    {/* Product Info */}
                    <div className="col-span-3 flex items-center gap-3">
                      <div className="relative">
                        <img
                          src={getImageUrl(product.product_image)}
                          alt={product.product_name}
                          className="w-14 h-14 object-cover rounded-lg border border-gray-200 flex-shrink-0 bg-gray-100"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTYiIGhlaWdodD0iNTYiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iMjgiIHk9IjI4IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5YTliYWIiPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                          }}
                        />
                        {product.new_arrival === 1 && (
                          <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full border border-white">
                            New
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {product.product_name}
                        </h3>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          SKU: {product.sku}
                        </p>
                        <p className="text-xs text-gray-400 truncate mt-0.5">
                          {product.product_description && product.product_description.substring(0, 40)}
                          {product.product_description && product.product_description.length > 40 ? '...' : ''}
                        </p>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="col-span-2 flex justify-center">
                      {getApprovalBadge(product.approval)}
                    </div>

                    {/* Inventory/Stock */}
                    <div className="col-span-2 text-center">
                      {getStockBadge(product.stock_quantity)}
                    </div>

                    {/* Category & Sub Category */}
                    <div className="col-span-2 text-center">
                      <div className="text-sm font-medium text-gray-900">
                        {product.catalogue_name || 'Uncategorized'}
                      </div>
                      {product.sub_catalogue_name && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {product.sub_catalogue_name}
                        </div>
                      )}
                    </div>

                    {/* Price */}
                    <div className="col-span-2 text-center">
                      <div className="text-lg font-bold text-gray-900">
                        ₹{product.sale_price || product.price || '0.00'}
                      </div>
                      {product.sale_price && product.sale_price !== product.price && (
                        <div className="text-xs text-gray-500 line-through mt-0.5">
                          ₹{product.price}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="col-span-1 flex justify-center gap-1">
                      <button
                        onClick={() => toggleProductExpansion(product.product_id)}
                        className={`p-2 rounded-lg transition-colors ${
                          isExpanded 
                            ? "bg-blue-100 text-blue-600" 
                            : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        }`}
                        title="View Details"
                      >
                        <FaEye className="text-sm" />
                      </button>
                      <Link
                        to={`/auth/edit-product/${product.product_id}`}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit Product"
                      >
                        <FaEdit className="text-sm" />
                      </Link>
                      <button
                        onClick={() => handleDeleteProduct(product)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Product"
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-6 py-4 bg-blue-50/30 border-t border-blue-100">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left Column - Details */}
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <FaEye className="text-blue-500" />
                              Product Details
                            </h4>
                            <p className="text-sm text-gray-600 bg-white p-3 rounded-lg border border-gray-200">
                              {product.product_description || 'No description available.'}
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white p-3 rounded-lg border border-gray-200">
                              <label className="text-xs font-semibold text-gray-700 mb-2 block">New Arrival</label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={product.new_arrival === 1}
                                  onChange={(e) =>
                                    handleNewArrivalChange(product.product_id, e.target.checked)
                                  }
                                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-600">Mark as new arrival</span>
                              </div>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-200">
                              <label className="text-xs font-semibold text-gray-700 mb-2 block">Update Status</label>
                              <select
                                value={product.approval}
                                onChange={(e) =>
                                  handleApprovalChange(product.product_id, e.target.value)
                                }
                                className="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="pending">Pending</option>
                                <option value="processing">Processing</option>
                                <option value="approved">Approved</option>
                                <option value="disapproved">Disapproved</option>
                              </select>
                            </div>
                          </div>

                          {/* Variants */}
                          {product.variants && product.variants.length > 0 && (
                            <div className="bg-white p-3 rounded-lg border border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-900 mb-2">Product Variants</h4>
                              <div className="space-y-2 max-h-32 overflow-y-auto">
                                {product.variants.map((variant, index) => (
                                  <div
                                    key={index}
                                    className="text-xs text-gray-600 bg-gray-50 p-2 rounded border border-gray-200"
                                  >
                                    <div className="font-semibold text-gray-800">{variant.option_value}</div>
                                    <div className="flex justify-between mt-1">
                                      <span>Price: ₹{variant.variant_prices}</span>
                                      <span>Stock: {variant.variant_stock_quantity || variant.stock_quantity}</span>
                                    </div>
                                    {variant.variant_sku && (
                                      <div className="text-gray-500">SKU: {variant.variant_sku}</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Right Column - Additional Info */}
                        <div className="space-y-4">
                          <div className="bg-white p-3 rounded-lg border border-gray-200">
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Category Information</h4>
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Main Category:</span>
                                <span className="text-sm font-medium text-gray-900">{product.catalogue_name || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Sub Category:</span>
                                <span className="text-sm font-medium text-gray-900">{product.sub_catalogue_name || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Product Type:</span>
                                <span className="text-sm font-medium text-gray-900 capitalize">{product.product_type || 'single'}</span>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white p-3 rounded-lg border border-gray-200">
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Pricing Information</h4>
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Regular Price:</span>
                                <span className="text-sm font-medium text-gray-900">₹{product.price || '0.00'}</span>
                              </div>
                              {product.sale_price && (
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">Sale Price:</span>
                                  <span className="text-sm font-medium text-green-600">₹{product.sale_price}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Stock Quantity:</span>
                                <span className="text-sm font-medium text-gray-900">{product.stock_quantity}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <div className="max-w-md mx-auto">
            <MdPendingActions className="mx-auto h-16 w-16 text-gray-300" />
            <h3 className="mt-4 text-xl font-semibold text-gray-900">No products found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {selectedApproval === "all" 
                ? "You haven't added any products yet. Start by adding your first product!" 
                : `No ${selectedApproval} products found. Try changing the filter.`
              }
            </p>
            <div className="mt-6 flex gap-3 justify-center">
              <Link
                to="/auth/add-product"
                className="inline-flex items-center gap-2 px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FaPlus />
                Add Your First Product
              </Link>
              {selectedApproval !== "all" && (
                <button
                  onClick={() => setSelectedApproval("all")}
                  className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  View All Products
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductList;